import { useState } from "react";
import { ACTIVE_STATUSES, type BuildingDto, type IssueSummary, type TaskDto } from "@klantpunt/shared";
import { Meta } from "../../app/Meta";
import { ConfirmDelete, Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { hasModule, useMe } from "../../lib/auth";
import { buyoutParts, useBreakdownLines } from "../contracts/useContracts";
import { parseEuro } from "../../pages/Platform";
import { PartyPicker, type Party } from "../directory/PartyPicker";
import { draftOf, saveTaskMaterials, TaskMaterialsEditor, type DraftLine } from "../materials/TaskMaterials";
import { usePeople, useTaskRefresh } from "./useTasks";

/** Nieuwe taak, taak bewerken, of een bevinding aan een bestaande taak koppelen (prototype `modalTask`). */
export function TaskDialog({
  building: b,
  issues,
  tasks,
  task,
  forIssue,
  onClose,
}: {
  building: BuildingDto;
  issues: IssueSummary[];
  tasks: TaskDto[];
  task?: TaskDto;
  /** Geopend vanuit een bevinding: die staat aangevinkt en bestaande open taken worden aangeboden. */
  forIssue?: string;
  onClose: () => void;
}) {
  const people = usePeople();
  const refresh = useTaskRefresh(b.id);
  const toast = useToast();
  const [title, setTitle] = useState(task?.title ?? "");
  // Lid, bedrijf/persoon uit het adresboek of een vrije naam (gedeelde kiezer).
  const [assignee, setAssignee] = useState<Party>({
    companyId: task?.assigneeCompanyId ?? null,
    personId: task?.assigneeCompanyId ? (task.assigneePersonId ?? null) : null,
    userId: task?.assigneeCompanyId ? null : (task?.assigneeUserId ?? null),
    text: task?.assigneeCompanyId || task?.assigneeUserId ? "" : (task?.assigneeText ?? ""),
  });
  const [due, setDue] = useState(task?.dueDate ?? "");
  const [cost, setCost] = useState(task?.costCents == null ? "" : (task.costCents / 100).toFixed(2).replace(".", ","));
  const costCents = cost.trim() ? parseEuro(cost) : null;
  const costInvalid = !!cost.trim() && costCents === null;
  const [issueIds, setIssueIds] = useState<string[]>(task?.issueIds ?? (forIssue ? [forIssue] : []));
  const [busy, setBusy] = useState(false);
  const { data: me } = useMe();
  const withMaterials = hasModule(me, "materials", "edit");
  const [materials, setMaterials] = useState<DraftLine[]>(() => draftOf(task));
  const materialsChanged = JSON.stringify(materials) !== JSON.stringify(draftOf(task));

  // Afkoop storingen (docs/CONTRACTEN.md): taak aan een storingspost; voorstel uit de asset van een bevinding.
  const withContracts = hasModule(me, "contracts");
  const breakdown = useBreakdownLines(b.id, withContracts);
  const lines = breakdown.data ?? [];
  const [lineId, setLineId] = useState<string | null>(task ? (task.contractLine?.lineId ?? "") : null);
  const assetIds = issues.filter((i) => issueIds.includes(i.id) && i.installationId).map((i) => i.installationId!);
  const suggested = lines.find((l) => l.assetIds.some((a) => assetIds.includes(a)));
  const chosenLineId = lineId ?? suggested?.lineId ?? "";
  const chosenLine = lines.find((l) => l.lineId === chosenLineId);
  const unassigned = !assignee.companyId && !assignee.userId && !assignee.personId && !assignee.text.trim();

  const existing = forIssue && !task ? tasks.filter((t) => t.status !== "klaar") : [];
  const candidates = issues
    .filter((i) => (ACTIVE_STATUSES.has(i.status) && (!i.taskId || i.taskId === task?.id)) || issueIds.includes(i.id))
    .sort((a, c) => a.priority.localeCompare(c.priority));

  /** fn geeft de melding terug (bv. met de nieuwe taakcode). */
  async function run(fn: () => Promise<string>) {
    setBusy(true);
    try {
      const ok = await fn();
      await refresh();
      toast(ok);
      onClose();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Er ging iets mis");
      setBusy(false);
    }
  }

  const body = () => ({
    title,
    assigneeUserId: assignee.userId,
    assigneeCompanyId: assignee.companyId,
    assigneePersonId: assignee.personId,
    assigneeText: assignee.text.trim() || null,
    dueDate: due || null,
    costCents,
    issueIds,
    ...(withContracts && breakdown.isSuccess ? { contractLineId: chosenLineId || null } : {}),
  });

  const save = () =>
    run(async () => {
      if (task) {
        await api(`/tasks/${task.id}`, { method: "PATCH", body: body() });
        if (withMaterials && materialsChanged) await saveTaskMaterials(task.id, materials);
        return `Taak ${task.code} opgeslagen`;
      }
      const t = await api<TaskDto>(`/buildings/${b.id}/tasks`, {
        method: "POST",
        body: { id: crypto.randomUUID(), ...body() },
      });
      if (withMaterials && materials.length) await saveTaskMaterials(t.id, materials);
      return `Taak ${t.code} opgeslagen`;
    });

  const linkExisting = (t: TaskDto) =>
    run(async () => {
      await api(`/tasks/${t.id}`, { method: "PATCH", body: { issueIds: [...new Set([...t.issueIds, forIssue!])] } });
      return `Gekoppeld aan taak ${t.code}`;
    });

  return (
    <Modal
      title={task ? `Taak ${task.code}` : "Nieuwe taak"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={busy || !title.trim() || costInvalid}>
            Opslaan
          </button>
        </>
      }
    >
      {existing.length > 0 && (
        <>
          <div className="q">
            <span className="ql">Of koppel aan een bestaande taak</span>
            <div className="choices">
              {existing.map((t) => (
                <button type="button" key={t.id} onClick={() => linkExisting(t)} disabled={busy}>
                  {t.code} · {t.title}
                  {t.assigneeName && <small>{t.assigneeName}</small>}
                </button>
              ))}
            </div>
          </div>
          <p className="label">Nieuwe taak</p>
        </>
      )}
      <label className="field">
        <span className="label">Titel *</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="bijv. Kluswacht ronde donderdag"
          maxLength={120}
        />
      </label>
      <div className="grid2">
        <PartyPicker
          id="task-assignee"
          value={assignee}
          onChange={setAssignee}
          savedName={task?.assigneeText}
          buildingId={b.id}
          users={people.data?.map((p) => ({ id: p.userId, name: p.name }))}
          emptyLabel="Nog niet toegewezen"
          placeholder="bijv. Loodgieter Jansen"
        />
        <label className="field">
          <span className="label">Uiterlijk</span>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
      </div>
      {(lines.length > 0 || task?.contractLine) && (
        <label className="field">
          <span className="label">Storingscontract</span>
          <select value={chosenLineId} onChange={(e) => setLineId(e.target.value)}>
            <option value="">Geen</option>
            {task?.contractLine && !lines.some((l) => l.lineId === task.contractLine!.lineId) && (
              <option value={task.contractLine.lineId}>
                {task.contractLine.contractName} · {task.contractLine.lineDescription}
              </option>
            )}
            {lines.map((l) => (
              <option key={l.lineId} value={l.lineId}>
                {l.contractName} · {l.lineDescription}
                {l.supplier ? ` · ${l.supplier.name}` : ""}
              </option>
            ))}
          </select>
          {chosenLine && (
            <span className="hint">
              <Meta
                items={[
                  ...buyoutParts(chosenLine),
                  chosenLine.billing === "fixed" && chosenLine.maxCallsPerYear != null
                    ? `${chosenLine.callsThisYear} van ${chosenLine.maxCallsPerYear} dit jaar`
                    : `${chosenLine.callsThisYear} dit jaar`,
                ]}
              />
              {!task && unassigned && chosenLine.supplier && ` ${chosenLine.supplier.name} wordt de uitvoerder.`}
              {lineId === null && suggested && " Voorgesteld: de asset valt onder deze post."}
            </span>
          )}
        </label>
      )}
      <label className="field">
        <span className="label">Kosten excl. btw</span>
        <input
          inputMode="decimal"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          placeholder="bijv. 450,00"
          aria-invalid={costInvalid || undefined}
        />
        <span className="hint">
          {costInvalid
            ? "Vul een bedrag in, zoals 450,00."
            : "Wat dit werk kostte, buiten een contract om. Telt mee in Inzichten bij de leverancier."}
        </span>
      </label>
      <fieldset className="q">
        <legend className="ql">Bevindingen in deze taak</legend>
        <div className="checklist">
          {candidates.map((i) => (
            <label key={i.id}>
              <input
                type="checkbox"
                checked={issueIds.includes(i.id)}
                onChange={(e) =>
                  setIssueIds(e.target.checked ? [...issueIds, i.id] : issueIds.filter((x) => x !== i.id))
                }
              />
              <span className={`chip prio prio-${i.priority}`}>{i.priority}</span>
              <span className="code small">{i.code}</span>
              <span>{i.title}</span>
            </label>
          ))}
          {candidates.length === 0 && <span className="hint">Geen open bevindingen zonder taak.</span>}
        </div>
      </fieldset>
      {withMaterials && <TaskMaterialsEditor buildingId={b.id} task={task} lines={materials} onChange={setMaterials} />}
      {task && (
        <>
          <p className="label">Verwijderen</p>
          <ConfirmDelete
            label="Taak verwijderen"
            question={`Taak ${task.code} verwijderen?`}
            busy={busy}
            onConfirm={() =>
              run(async () => {
                await api(`/tasks/${task.id}`, { method: "DELETE" });
                return `Taak ${task.code} verwijderd`;
              })
            }
            detail="De bevindingen blijven bestaan; bevindingen die in opdracht staan gaan terug naar Open."
          />
        </>
      )}
    </Modal>
  );
}
